import React, { useState } from 'react';
import {
  BirdzModal,
  BirdzModalActions,
  BirdzModalContent,
  BirdzModalTitle,
  themeColors,
  useGlobalContext
} from '@applications-terrains/birdz-react-library';
import {
  Box,
  Button,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { exportToCSV, exportToExcel } from '../../../NewIntervention/Steps/PDSSelection/Templates';
import {
  ImportTemplate,
  buildImportTemplates
} from '../../../NewIntervention/Steps/PDSSelection/importTemplates';
import ImportKindToggle, { ImportKind } from './ImportKindToggle';

/** Format du fichier téléchargé, qui donne aussi son extension. */
type TemplateFormat = 'csv' | 'excel';

const EXTENSIONS: Record<TemplateFormat, string> = { csv: 'csv', excel: 'xlsx' };

type ImportTemplatesModalProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Modèles de fichiers d'import, au design de la refonte.
 *
 * Les modèles eux-mêmes sont ceux de l'ancien écran : seule leur présentation change.
 * Le `Type` réutilise `ImportKindToggle`, dont les deux libellés sont déjà ceux de la
 * maquette.
 */
export default function ImportTemplatesModal({ open, onClose }: ImportTemplatesModalProps) {
  const { t } = useTranslation();
  // La maquette montre un titre orange : c'est la couleur d'accentuation du thème
  // choisi par l'utilisateur, comme sur la modale d'ajout.
  const { theme } = useGlobalContext();
  const [kind, setKind] = useState<ImportKind>('maintenance');
  const [format, setFormat] = useState<TemplateFormat>('csv');

  const templates = buildImportTemplates(t);

  const fileName = (template: ImportTemplate) => `${template.fileBaseName}.${EXTENSIONS[format]}`;

  const download = (template: ImportTemplate) => {
    // Les exports attendent la feuille entière : ici une seule ligne, l'en-tête.
    const sheet = [template.columns];
    const name = fileName(template);

    if (format === 'csv') {
      exportToCSV(sheet, name);
      return;
    }

    exportToExcel(sheet, name);
  };

  return (
    <BirdzModal open={open} onClose={onClose} maxWidth="sm">
      <BirdzModalTitle onClose={onClose}>
        {/* Voir `AddServicePointsModal` : les types publiés de BirdzModalTitle n'exposent
            pas `sx`, on colore donc le libellé plutôt que le conteneur. */}
        <Typography
          component="span"
          sx={{ color: themeColors[theme], fontWeight: 700, fontSize: 'inherit' }}
        >
          {t('interventionCreation.addServicePoints.templates.title')}
        </Typography>
      </BirdzModalTitle>

      <BirdzModalContent>
        <Stack spacing={3}>
          <ImportKindToggle
            value={kind}
            onChange={setKind}
            labelKey="interventionCreation.addServicePoints.templates.type"
          />

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body2">
              {t('interventionCreation.addServicePoints.templates.format')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={format}
              // MUI renvoie null quand on reclique sur la pastille active : l'un des
              // deux formats doit toujours rester choisi.
              onChange={(_, next: TemplateFormat | null) => next && setFormat(next)}
            >
              <ToggleButton value="csv">
                {t('interventionCreation.addServicePoints.templates.csv')}
              </ToggleButton>
              <ToggleButton value="excel">
                {t('interventionCreation.addServicePoints.templates.excel')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack spacing={1}>
            {templates[kind].map((template) => (
              <Box
                key={template.fileBaseName}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover'
                }}
              >
                <DescriptionOutlinedIcon fontSize="small" color="action" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t(template.labelKey)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fileName(template)}
                  </Typography>
                </Box>
                <Tooltip title={t('interventionCreation.addServicePoints.templates.download')}>
                  <IconButton
                    color="primary"
                    aria-label={t('interventionCreation.addServicePoints.templates.download')}
                    onClick={() => download(template)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        </Stack>
      </BirdzModalContent>

      <BirdzModalActions>
        <Button variant="outlined" onClick={onClose}>
          {t('interventionCreation.addServicePoints.close')}
        </Button>
      </BirdzModalActions>
    </BirdzModal>
  );
}
